/*
 * Copyright (c) 2023 - 2026. Bluemate B.V.
 */
import './style/sendInvoice.scss';

import {AwesomeIcon, ChangeFunction, ErrorType, Loader} from '@dvrd/dvr-controls';

import React, {useContext, useEffect, useRef, useState} from 'react';

import {useTranslation} from 'react-i18next';

import EmailEditor, {EditorRef} from 'react-email-editor';

import {getMergeTags} from '../../../util/unlayerUtil';

import {AppContext} from '../../misc/appContext';

import {BluemateDialog, BluemateInput} from '../../controls';

import Invoice from '../../../models/invoiceModel';

import {UnlayerDesign} from '../../../util/interfaces';
import simpleInvoiceTemplate from '../../../../res/static/emailTemplates/invoice-simple.json';

interface Props {
    onClose: VoidFunction;
    onSubmit: (data: {design: UnlayerDesign; html: string}) => void;
    onChangeSubject: ChangeFunction;
    active: boolean;
    subject: string;
    loading: boolean;
    invoice: Invoice | null;
    error: ErrorType;
}

export default function SendInvoice(props: Props) {
    const {t} = useTranslation();
    const context = useContext(AppContext);
    const {active, onChangeSubject, subject, onSubmit, onClose, loading, invoice, error} = props;
    const editorRef = useRef<EditorRef>(null);
    const [loadingEditor, setLoadingEditor] = useState(true);

    function onEditorLoaded() {
        setLoadingEditor(false);
    }

    function _onSubmit() {
        if (!editorRef.current) return;
        editorRef.current?.editor?.exportHtml((data: {design: UnlayerDesign; html: string}) => {
            onSubmit(data);
        });
    }

    function setDesign() {
        if (!editorRef.current) return;
        let design: UnlayerDesign | null = getDesignFromAccount();
        if (!design) design = simpleInvoiceTemplate as unknown as UnlayerDesign;
        editorRef.current?.editor?.loadDesign(design!);
        editorRef.current?.editor?.setMergeTags(getMergeTags(context.accountContext.account));
    }

    function getDesignFromAccount(): UnlayerDesign | null {
        const manualMailJson = context.accountContext.account?.settings?.manualMailJson;
        if (!manualMailJson || !Object.keys(manualMailJson).length) return null;
        return manualMailJson;
    }

    function renderInvoiceDetails() {
        return (
            <div className='invoice-details'>
                <div className='field'>
                    <label className='key'>{t('invoice.send.orderNumber', 'Ordernummer:')}</label>
                    <label>{invoice?.orderId}</label>
                </div>
                <div className='field'>
                    <label className='key'>{t('invoice.send.invoiceNumber', 'Factuurnummer:')}</label>
                    <label>{invoice?.invoiceNum}</label>
                </div>
                <div className='field'>
                    <label className='key'>{t('invoice.send.invoiceDate', 'Factuurdatum:')}</label>
                    <label>{invoice?.invoiceDate.format('DD-MM-YYYY')}</label>
                </div>
                <div className='field'>
                    <label className='key'>{t('invoice.send.customer', 'Klant:')}</label>
                    <label>{invoice?.invoiceName}</label>
                </div>
            </div>
        );
    }

    useEffect(() => {
        if (!loadingEditor && active) setDesign();
        if (!active) setLoadingEditor(true);
    }, [loadingEditor, active]);

    return (
        <BluemateDialog
            active={active}
            onClose={onClose}
            title={t('invoice.send.title', 'Factuur verzenden')}
            className='send-invoice-dialog'
            width='64rem'
            onConfirm={_onSubmit}
            confirmLabel={t('invoice.send.send', 'Verzenden')}
            confirmDisabled={loadingEditor}
            cancelVariant='tertiary'
            loading={loading}
        >
            <Loader active={loading} />
            {renderInvoiceDetails()}
            <div className='email-container'>
                <BluemateInput
                    onChange={onChangeSubject}
                    value={subject}
                    label={t('invoice.send.subjectRequired', 'Onderwerp *')}
                    shape='rounded'
                    uniformFont
                    inputProps={{placeholder: t('invoice.send.subjectPlaceholder', 'Onderwerp van de e-mail')}}
                />
                <div className='editor-container'>
                    <EmailEditor
                        ref={editorRef}
                        onReady={onEditorLoaded}
                        projectId={43580}
                        minHeight={400}
                        options={{
                            features: {
                                smartMergeTags: false
                            }
                        }}
                    />
                </div>
            </div>
            <label className='attachment-label'>
                <AwesomeIcon name='paperclip' />{' '}
                <span className='bold'>{t('invoice.send.attachment', 'Bijlage:')}</span> {invoice?.orderId}.pdf
            </label>
            <label className='disclaimer'>
                {t(
                    'invoice.send.disclaimer',
                    'Voor deze e-mail worden niet de instellingen van de E-mails pagina gebruikt.'
                )}
            </label>
            {!!error && <label className='error'>{error}</label>}
        </BluemateDialog>
    );
}
